import { useCallback, useState } from 'react';

export interface AudioUploadResult {
  s3Key: string;
  url: string;
  fileName: string;
  duration?: number;
}

export function useAudioUpload(authedFetch: typeof fetch) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(async (file: File): Promise<AudioUploadResult | null> => {
    setError(null);
    setUploading(true);
    try {
      const presignRes = await authedFetch('/api/editor-assets/audio/presign', {
        method: 'POST',
        body: JSON.stringify({ fileName: file.name, fileType: file.type, fileSize: file.size }),
      });
      const presign = await presignRes.json();
      if (!presignRes.ok || !presign?.success) {
        throw new Error(presign?.error || 'Presign failed');
      }

      const putRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
          // Match presigned CacheControl so S3 actually persists the header.
          'Cache-Control': 'public, max-age=31536000, immutable',
        },
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);

      const duration = await new Promise<number | undefined>((resolve) => {
        const audio = new Audio();
        const objectUrl = URL.createObjectURL(file);
        const cleanup = () => {
          try {
            URL.revokeObjectURL(objectUrl);
          } catch {}
        };
        audio.preload = 'metadata';
        audio.onloadedmetadata = () => {
          const value = Number.isFinite(audio.duration) ? audio.duration : undefined;
          cleanup();
          resolve(value);
        };
        audio.onerror = () => {
          cleanup();
          resolve(undefined);
        };
        audio.src = objectUrl;
      });

      return { s3Key: presign.key, url: presign.url, fileName: file.name, duration };
    } catch (e: any) {
      setError(e?.message || 'Upload failed');
      return null;
    } finally {
      setUploading(false);
    }
  }, [authedFetch]);

  return { upload, uploading, error };
}
